// Toolbar.js — Shared glassmorphism toolbar components
import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import './Toolbar.css';

/* ─── Glassmorphic Tooltip (portal-based) ─── */
function TbTooltip({ text, anchorRef, visible }) {
  const [pos, setPos] = useState({ top: 0, left: 0, placement: 'above', shift: 0 });
  const tipRef = useRef(null);

  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const update = () => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const spaceAbove = r.top;
      const placement = spaceAbove > 50 ? 'above' : 'below';
      const centerLeft = r.left + r.width / 2;
      const tipW = tipRef.current ? tipRef.current.offsetWidth : 0;
      // Centered tooltip (translateX(-50%)) — nudge it back on-screen by adding a horizontal
      // `shift` so it never overhangs the right (or left) edge of the viewport.
      let shift = 0;
      if (tipW) {
        const overRight = centerLeft + tipW / 2 - (window.innerWidth - 8);
        const overLeft = 8 - (centerLeft - tipW / 2);
        if (overRight > 0) shift = -overRight;
        else if (overLeft > 0) shift = overLeft;
      }
      setPos({
        top: placement === 'above' ? r.top - 6 : r.bottom + 6,
        left: centerLeft + shift,
        placement,
      });
    };
    update();
    const raf = requestAnimationFrame(update);
    return () => cancelAnimationFrame(raf);
  }, [visible, anchorRef]);

  if (!visible || !text) return null;
  return ReactDOM.createPortal(
    <div
      ref={tipRef}
      className={`tb-tooltip tb-tooltip-${pos.placement}`}
      style={{ top: pos.top, left: pos.left }}
    >
      {text}
    </div>,
    document.body
  );
}

function useTbTooltip() {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const onEnter = useCallback(() => { timer.current = setTimeout(() => setShow(true), 400); }, []);
  const onLeave = useCallback(() => { clearTimeout(timer.current); setShow(false); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { ref, show, onEnter, onLeave };
}

/* ─── App Logo (the Melbourne City crest) ─── */
export function TbLogo() {
  return (
    <Link to="/" className="tb-logo" title="Melbourne City Insights">
      <img src="/melbourne-city-crest.png" alt="Melbourne City" width="20" height="20" />
    </Link>
  );
}

/* ─── Simplified accent ring for toolbar buttons ─── */
export function TbGoldRing() {
  return (
    <>
      <span className="tb-gold-glow" />
      <span className="tb-gold-ring" />
      <span className="tb-gold-plate" />
    </>
  );
}

/* ─── Icon Button with optional accent ring ─── */
/* `data-tbtip` carries the tooltip's text on the button itself. The tooltip proper is a React
   portal, so it cannot survive the DOM snapshot a standalone HTML export takes — the exported
   file's own script rebuilds the same `.tb-tooltip` from this attribute instead (see
   attackingP3Export.js). It is NOT `title=`, which would put the browser's own yellow box on
   screen alongside the glass one. */
export function TbIconBtn({ active, onClick, title, children, className = '', style, ...rest }) {
  const tt = useTbTooltip();
  return (
    <button
      ref={tt.ref}
      className={`tb-icon-btn ${active ? 'active' : ''} ${className}`}
      onClick={onClick}
      style={style}
      data-tbtip={title || undefined}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
      {...rest}
    >
      {active && <TbGoldRing />}
      {children}
      <TbTooltip text={title} anchorRef={tt.ref} visible={tt.show} />
    </button>
  );
}

/* ─── Toggle Group (2-3 icon/text buttons) ─── */
export function TbToggleGroup({ children }) {
  return <div className="tb-toggle-group">{children}</div>;
}

/* `className` is taken by NAME rather than left in `...rest`, and merged.
   Spread after the button's own className, a caller's class REPLACED it — so a page that wanted
   to tint one button ("Scored" green, "Conceded" rust) lost the padding, the pill, the type scale
   and the active state along with it, and two unstyled 13px buttons in a nowrap toolbar pushed
   every picker beside them down to nothing. Nobody passing a class is asking for that. */
export function TbToggleBtn({ active, onClick, title, children, className = '', ...rest }) {
  const tt = useTbTooltip();
  return (
    <button
      ref={tt.ref}
      className={`tb-toggle-btn ${active ? 'active' : ''}${className ? ` ${className}` : ''}`}
      onClick={onClick}
      data-tbtip={title || undefined}
      onMouseEnter={tt.onEnter}
      onMouseLeave={tt.onLeave}
      {...rest}
    >
      {active && <TbGoldRing />}
      <span style={{ position: 'relative', zIndex: 5, display: 'flex', alignItems: 'center', gap: '4px' }}>
        {children}
      </span>
      <TbTooltip text={title} anchorRef={tt.ref} visible={tt.show} />
    </button>
  );
}

/* ─── Divider ─── */
export function TbDivider() {
  return <div className="tb-divider" />;
}

/* ─── Toolbar Bar Container ─── */
/* On a phone the bar wraps into as many rows as its controls need and sticks to the top of the
   scroll (mobile.css). A bar of two rows is a header; a bar of five — the load-tracking reports
   measure 270px on an iPhone — is a third of the screen permanently covering the report it
   controls. So a bar is measured, and one taller than a header is marked `tb-bar-tall`, which
   mobile.css lets scroll away with the page instead of sticking. Desktop bars never wrap and are
   never tall, so the class does nothing there. */
const TALL_BAR_PX = 110;
export function TbBar({ children, className = '', style }) {
  const ref = useRef(null);
  const [tall, setTall] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTall(el.offsetHeight > TALL_BAR_PX));
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);
  return <div ref={ref} className={`tb-bar ${tall ? 'tb-bar-tall ' : ''}${className}`} style={style}>{children}</div>;
}
